import { findIndex, groupBy, isEmpty, keyBy } from 'lodash';
import { useCallback } from 'react';
import { FieldArrayPathValue } from 'react-hook-form';

import { useFormContext } from '@/components/react-hook-form';
import {
  AISuggestionsMatcherArrayField,
  ArrayFieldElement,
} from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.types';
import { CombinedSupportedSubformFieldTypes } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { AiSuggestionKind } from '@/types/schema';
import { PathsOf } from '@/types/subform';
import { diagnostics } from '@/utils/diagnostics';

// An object that describes the array field to update in the subform, and new
// inputs to add / merge into that array field.
export interface ArrayFieldToUpdate extends ArrayFieldWithID {
  // New inputs to add / merge this array field
  newInputs: FieldArrayPathValue<
    CombinedSupportedSubformFieldTypes,
    AISuggestionsMatcherArrayField
  >;
  // The function to call to get the default value for the array field.
  // It is fine for this to return undefined, since it only applies to
  // fields that _could_ have duplicates. An error will be logged if this is
  // expected to return a value, but returns undefined (not a super big deal).
  defaultValueFn: () => ArrayFieldElement | undefined;
}

// An object that describes the array field in a subform, including the idField
// that is used to identify one input element.
export interface ArrayFieldWithID {
  // The full path to the array field in the entity form, e.g., 'trustDetailsSubform.trustees'
  arrayFieldFullPath: AISuggestionsMatcherArrayField;
  // The id field of the input element that represents the value e.g., 'id' or 'clientProfileId'
  idField: string;
  // The label to show when there are duplicate input elements in the array field.
  // This only applies for fields that _could_ have duplicates: trustees, successorTrustees, trustAdvisors, beneficiaries
  duplicateGroupLabel?: string;
  // If true, after applying the form updates, will not append a placeholder input.
  omitPlaceholder?: boolean;
  // The insertion order to use when adding new items. Default is 'append'.
  insertionOrder?: 'prepend' | 'append';
  // Some form arrays can validly contain multiple rows with the same idField.
  // For example, DP recipients may repeat the same recipient across different
  // disposition orders, so duplicate ids should not be treated as conflicts.
  allowDuplicateIds?: boolean;
  // Overrides the variant-level `subformArrayFieldOnConflict` for this specific
  // array field. This lets a single variant mix strategies — e.g.
  // BUSINESS_PEOPLE appends/dedupes key people but overwrites beneficial
  // owners, whose equal-split defaults would otherwise trigger a noisy
  // duplicate-resolution flow on every accepted suggestion.
  onConflict?: 'overwrite' | 'append';
  // The suggestion kinds that populate this array field. Used to look up the
  // effective conflict strategy for a given suggestion in the matcher row UI.
  suggestionKinds?: AiSuggestionKind[];
  // When set, `appendArrayFields` stable-sorts the combined array by this numeric
  // field (ascending) after merging in the new inputs. Elements missing the field
  // sort to the end. Used for DP recipients so accepting suggestions out of order
  // still lands them in the document's disposition order, and pushes existing
  // (non-suggestion) recipients down.
  sortByNumericField?: string;
}

interface UseUpdateSubformArrayFieldReturn {
  // Given an array of updates, this function will update the entries of array
  // fields in a form. If there is an existing item in the form's array field
  // with the same id, the new input will overwrite the existing one.
  // This will reset the form to trigger a re-render and block navigation.
  updateArrayFields: (updates: ArrayFieldToUpdate[]) => void;

  // Given an array of updates, this function will update the entries of array
  // fields in a form. This will append the new inputs to the form's array
  // fields. This will reset the form to trigger a re-render and block navigation.
  // Returns any array fields that have duplicates, along with the number of
  // duplicates.
  appendArrayFields: (
    updates: ArrayFieldToUpdate[]
  ) => Partial<Record<AISuggestionsMatcherArrayField, number>>;

  // Given an array of array fields in a form, return the first array field that
  // has duplicate input elements, based on the value of the idField.
  getFirstDuplicateFieldName: (
    arrayFields: ArrayFieldWithID[]
  ) => PathsOf<CombinedSupportedSubformFieldTypes> | undefined;
}

/**
 * A hook that exposes a function to update the entries of array fields in a form.
 */
export function useUpdateSubformArrayField(): UseUpdateSubformArrayFieldReturn {
  const { getValues, setValue, setShouldBlockNavigation } =
    useFormContext<CombinedSupportedSubformFieldTypes>();

  const updateArrayFields = useCallback(
    (updates: ArrayFieldToUpdate[]) => {
      if (isEmpty(updates)) return;

      updates.forEach((update) => {
        const { arrayFieldFullPath, newInputs, idField } = update;

        const currentValuesById = keyBy(getValues(arrayFieldFullPath), idField);
        const newValuesById = keyBy(newInputs, idField);

        // Merge the current array values with the new array values.
        const mergedValuesById = {
          ...currentValuesById,
          ...newValuesById,
        };

        // Remove the placeholder input element, if we have at least 1 "real"
        // input element now.
        let nonEmptyIds = Object.keys(mergedValuesById);
        if (Object.keys(mergedValuesById).length > 1) {
          nonEmptyIds = Object.keys(mergedValuesById).filter(
            (key) => key !== ''
          );
        }

        const newValues = nonEmptyIds.map((id) => mergedValuesById[id]);

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // We are ignoring the type error here because the data types between
        // useFormContext and useFieldArray are not compatible, unfortunately.
        setValue(arrayFieldFullPath, newValues, { shouldDirty: true });
      });

      setShouldBlockNavigation(true);
    },
    [getValues, setShouldBlockNavigation, setValue]
  );

  const appendArrayFields = useCallback(
    (
      updates: ArrayFieldToUpdate[]
    ): Partial<Record<AISuggestionsMatcherArrayField, number>> => {
      if (isEmpty(updates)) return {};

      const arrayFieldsWithDuplicates: Partial<
        Record<AISuggestionsMatcherArrayField, number>
      > = {};

      updates.forEach((update) => {
        const {
          arrayFieldFullPath,
          newInputs,
          idField,
          defaultValueFn,
          allowDuplicateIds,
          sortByNumericField,
        } = update;

        const combined =
          update.insertionOrder === 'prepend'
            ? [...(newInputs || []), ...(getValues(arrayFieldFullPath) || [])]
            : [...(getValues(arrayFieldFullPath) || []), ...(newInputs || [])];

        let duplicates: string[] = [];
        if (!allowDuplicateIds) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          // We are ignoring the type error here because the data types between
          // useFormContext and useFieldArray are not compatible, unfortunately.
          duplicates = getDuplicates(combined, idField);
          if (!isEmpty(duplicates)) {
            arrayFieldsWithDuplicates[arrayFieldFullPath] = duplicates.length;
          }
        }

        const nonDuplicatedInputs = allowDuplicateIds
          ? (combined || []).filter(
              (input) => input[idField as keyof typeof input] !== ''
            )
          : (combined || []).filter(
              (input) =>
                !duplicates.includes(input[idField as keyof typeof input]) &&
                input[idField as keyof typeof input] !== ''
            );

        // Re-sort the merged inputs by the configured numeric field so that
        // accepting suggestions out of order still lands them in document order.
        // Inputs missing the field (e.g. manually-added recipients) sort last.
        if (sortByNumericField) {
          sortByNumericFieldAscending(nonDuplicatedInputs, sortByNumericField);
        }
        const duplicatedInputs = allowDuplicateIds
          ? []
          : (combined || []).filter((input) =>
              duplicates.includes(input[idField as keyof typeof input])
            );

        // If there are no non-duplicate input elements, prepend a placeholder
        // (unless specified to omit it).
        const newValues = [...nonDuplicatedInputs, ...duplicatedInputs];
        if (nonDuplicatedInputs.length === 0 && !update.omitPlaceholder) {
          const placeholder = defaultValueFn();
          if (!placeholder) {
            diagnostics.info(
              `[useUpdateSubformArrayField][appendArrayFields] no defaultValueFn was provided for array field: ${arrayFieldFullPath}. not adding placeholder`
            );
          } else {
            newValues.push(placeholder);
          }
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // We are ignoring the type error here because the data types between
        // useFormContext and useFieldArray are not compatible, unfortunately.
        setValue(arrayFieldFullPath, newValues, { shouldDirty: true });
      });

      setShouldBlockNavigation(true);

      return arrayFieldsWithDuplicates;
    },
    [getValues, setShouldBlockNavigation, setValue]
  );

  const getFirstDuplicateFieldName = useCallback(
    (
      arrayFields: ArrayFieldWithID[]
    ): PathsOf<CombinedSupportedSubformFieldTypes> | undefined => {
      if (isEmpty(arrayFields)) return undefined;

      let dupeId: string | undefined;
      let dupeArrayField: ArrayFieldWithID | undefined;

      arrayFields.forEach((field) => {
        const valuesById = groupBy(
          getValues(field.arrayFieldFullPath),
          field.idField
        );

        Object.entries(valuesById).forEach(([key, values]) => {
          if (values.length > 1) {
            dupeId = key;
            dupeArrayField = field;
          }
          if (dupeId && dupeArrayField) {
            return;
          }
        });

        if (dupeId && dupeArrayField) {
          return;
        }
      });

      if (!dupeId || !dupeArrayField) return undefined;

      const { arrayFieldFullPath, idField } = dupeArrayField;
      const values = getValues(arrayFieldFullPath);

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // We are ignoring the type error here because the data types between
      // useFormContext and useFieldArray are not compatible, unfortunately.
      const index = findIndex(values, (v) => v[idField] === dupeId);
      if (index === -1) return undefined;

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // We are ignoring the type error here because the data types between
      // useFormContext and useFieldArray are not compatible, unfortunately.
      return `${arrayFieldFullPath}.${index}.${idField}`;
    },
    [getValues]
  );

  return {
    updateArrayFields,
    appendArrayFields,
    getFirstDuplicateFieldName,
  };
}

/**
 * Stable-sorts `values` in place by the numeric `field`, ascending. Elements
 * whose field is null/undefined/non-numeric are treated as `Infinity` and sink
 * to the end. Array.prototype.sort is stable, so elements that compare equal
 * (including all the "missing" ones) keep their relative order.
 */
export function sortByNumericFieldAscending(
  values: unknown[],
  field: string
): void {
  values.sort((a, b) => {
    const orderA = readNumericField(a, field);
    const orderB = readNumericField(b, field);
    // Explicit equality check to avoid Infinity - Infinity = NaN
    if (orderA === orderB) return 0;
    return orderA - orderB;
  });
}

function readNumericField(value: unknown, field: string): number {
  if (value && typeof value === 'object' && field in value) {
    const raw = (value as Record<string, unknown>)[field];
    if (typeof raw === 'number') return raw;
  }
  return Infinity;
}

/**
 * Given an array of values and an idField, returns the id values of the
 * duplicated item. Duplicates are determined by the value of the idField,
 * which is like "trusteeId", where the value is CP_123ABC.
 */
function getDuplicates(
  values: FieldArrayPathValue<
    CombinedSupportedSubformFieldTypes,
    AISuggestionsMatcherArrayField
  >,
  idField: string
): string[] {
  const duplicates: string[] = [];
  const valuesById = groupBy(values, idField);
  Object.entries(valuesById).forEach(([key, values]) => {
    if (values.length > 1) {
      duplicates.push(key);
    }
  });
  return duplicates;
}
